import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { ApolloClient, gql } from '@apollo/client';
import { GET_QARULE_BY_ID, UPDATE_QARULE } from './managerHome.graphql';
import {
  redistributeEqualVolume,
  calculateSpecificPercentageRemainingVolume,
  calculateSpecificCountRemainingVolume,
  validAssignedVolume,
  validSubmittedVolume,
  type AssigneeEntry,
} from './assignmentUtils';
import { getAuthHeaders } from './utils';
import { fetchApi } from '@codametrix/client-api';

const QA_RULE_UPDATED_EVENT = 'qa-rule-updated';

const CodersByGroups = gql`
  query CodersByGroups($groupIds: [String]!) {
    CodersByGroups(groupIds: $groupIds) {
      data
      total
    }
  }
`;

interface CoderOption {
  label: string;
  value: string;
  username: string;
}

type DistributionVolume = 'EQUAL_PERCENTAGE' | 'SPECIFIC_VOLUME';
type VolumeType = 'PERCENTAGES' | 'COUNT';

interface ProjectMetadata {
  assignable_count: number;
  unassigned_count: number;
  processing_count: number;
}

function toModalDistributionVolume(stored?: string): DistributionVolume {
  if (stored === 'equalPercentages' || stored === 'EQUAL_PERCENTAGE') return 'EQUAL_PERCENTAGE';
  if (stored === 'specificVolume' || stored === 'SPECIFIC_VOLUME') return 'SPECIFIC_VOLUME';
  return 'EQUAL_PERCENTAGE';
}

function toModalVolumeType(stored?: string): VolumeType | '' {
  if (stored === 'Percentages' || stored === 'PERCENTAGES') return 'PERCENTAGES';
  if (stored === 'Case Count' || stored === 'COUNT') return 'COUNT';
  return '';
}

export interface AssignCodersModalProps {
  isOpen: boolean;
  onClose: () => void;
  ruleId: string;       // work_item.rule_id → cqa-service metadata + assign-coders endpoints
  collectionId: string; // collection_id → QaruleById + UpdateQarule (directory service entry identifier)
  projectName: string;
  groupId: string;
  apolloClient: ApolloClient<any>;
}

export const AssignCodersModal: React.FC<AssignCodersModalProps> = ({
  isOpen,
  onClose,
  ruleId,
  collectionId,
  projectName,
  groupId,
  apolloClient,
}) => {
  const [projectConfiguration, setProjectConfiguration] = useState<Record<string, any> | null>(null);
  // project_compare = multi-grader compare mode: every coder gets all cases, no volume split
  const isCompareMode = projectConfiguration?.typeOfWork === 'project_compare';
  const [coders, setCoders] = useState<CoderOption[]>([]);
  const [assignees, setAssignees] = useState<AssigneeEntry[]>([]);
  const [distributionVolume, setDistributionVolume] = useState<DistributionVolume>('EQUAL_PERCENTAGE');
  const [volumeType, setVolumeType] = useState<VolumeType | ''>('');
  const [metadata, setMetadata] = useState<ProjectMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coderQuery, setCoderQuery] = useState('');
  const [isCoderListOpen, setIsCoderListOpen] = useState(false);
  const toast = useToast();

  const disableDistribution =
    metadata?.assignable_count === 0 || (metadata?.processing_count ?? 0) > 0;

  const remainingVolume =
    volumeType === 'PERCENTAGES'
      ? calculateSpecificPercentageRemainingVolume(assignees)
      : volumeType === 'COUNT'
        ? calculateSpecificCountRemainingVolume(assignees, metadata?.assignable_count ?? 0)
        : 0;

  const fetchQaRule = useCallback(async () => {
    try {
      const response = await apolloClient.query({
        query: GET_QARULE_BY_ID,
        variables: { id: collectionId },
        fetchPolicy: 'no-cache',
      });
      const projectConfig = response.data?.QaruleById?.data?.project_configuration ?? null;
      setProjectConfiguration(projectConfig);
    } catch (err) {
      console.error('Error fetching qaRule:', err);
    }
  }, [apolloClient, collectionId]);

  const fetchCoders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apolloClient.query({
        query: CodersByGroups,
        variables: { groupIds: [groupId] },
        fetchPolicy: 'no-cache',
      });
      const data = response.data?.CodersByGroups?.data ?? [];
      const mapped: CoderOption[] = data
        .map((worker: any) => ({
          label: `${worker.data.first_name} ${worker.data.last_name}`,
          value: worker.data.worker_id,
          username: worker.data.username ?? '',
        }))
        .sort((a: CoderOption, b: CoderOption) => a.label.localeCompare(b.label));
      setCoders(mapped);
    } catch (err) {
      console.error('Error fetching coders:', err);
      toast({ title: 'Failed to load coders', status: 'error', duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  }, [apolloClient, groupId, toast]);

  const fetchMetadata = useCallback(async () => {
    setIsMetadataLoading(true);
    try {
      const response = await fetchApi(`/api/cqa-service/v1/project/${ruleId}/metadata`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!response.ok) throw new Error(`metadata ${response.status}`);
      const data: ProjectMetadata = await response.json();
      setMetadata(data);
    } catch (err) {
      console.error('Error fetching project metadata:', err);
      toast({ title: 'Could not load project metadata', status: 'warning', duration: 3000 });
    } finally {
      setIsMetadataLoading(false);
    }
  }, [ruleId, toast]);

  // On open: reset transient state and kick off all three fetches in parallel
  useEffect(() => {
    if (isOpen) {
      setProjectConfiguration(null);
      setAssignees([]);
      setDistributionVolume('EQUAL_PERCENTAGE');
      setVolumeType('');
      setMetadata(null);
      setCoderQuery('');
      setIsCoderListOpen(false);
      fetchQaRule();
      fetchCoders();
      fetchMetadata();
    }
  }, [isOpen, fetchQaRule, fetchCoders, fetchMetadata]);

  // Pre-populate form once projectConfiguration is loaded (re-assign flow)
  useEffect(() => {
    if (!projectConfiguration) return;
    const audience = projectConfiguration.audience ?? {};
    const existingIndividuals: any[] = Array.isArray(audience.individuals) ? audience.individuals : [];
    if (existingIndividuals.length > 0) {
      setAssignees(
        existingIndividuals.map((ind: any) => ({
          value: ind.value ?? '',
          label: ind.label ?? '',
          volume: ind.volume ?? 0,
        })),
      );
      setDistributionVolume(toModalDistributionVolume(audience.distributionVolume));
      setVolumeType(toModalVolumeType(audience.volumeType));
    }
  }, [projectConfiguration]);

  const handleUserSelect = (coder: CoderOption) => {
    const newEntry: AssigneeEntry = { label: coder.label, value: coder.value, volume: 0 };
    const updated = [...assignees, newEntry];

    if (!isCompareMode && distributionVolume === 'EQUAL_PERCENTAGE') {
      redistributeEqualVolume(updated);
    }

    setAssignees(updated);
    // Clear the query but keep the list open and focused so the user can
    // add one or more additional coders without re-focusing the input.
    setCoderQuery('');
    setIsCoderListOpen(true);
  };

  const handleUserRemove = (entry: AssigneeEntry) => {
    const updated = assignees.filter((a) => a.value !== entry.value);
    if (!isCompareMode && distributionVolume === 'EQUAL_PERCENTAGE') {
      redistributeEqualVolume(updated);
    }
    setAssignees(updated);
  };

  const handleVolumeChange = (entry: AssigneeEntry, _valStr: string, valNum: number) => {
    const safeVal = isNaN(valNum) ? 0 : valNum;
    const updated = assignees.map((a) =>
      a.value === entry.value ? { ...a, volume: safeVal } : { ...a }
    );
    if (!validAssignedVolume(updated, volumeType as VolumeType, metadata?.assignable_count ?? 0)) {
      toast({ title: 'Total volume exceeds maximum', status: 'warning', duration: 2500 });
      return;
    }
    setAssignees(updated);
  };

  const handleDistributionVolumeChange = (val: string) => {
    setAssignees([]);
    setVolumeType('');
    setCoderQuery('');
    setDistributionVolume(val as DistributionVolume);
  };

  const handleVolumeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAssignees([]);
    setCoderQuery('');
    setVolumeType(e.target.value as VolumeType);
  };

  const handleSubmit = async () => {
    if (assignees.length === 0 || disableDistribution) return;

    if (!isCompareMode) {
      if (distributionVolume === 'SPECIFIC_VOLUME' && !volumeType) {
        toast({ title: 'Please select a volume type', status: 'warning', duration: 3000 });
        return;
      }

      if (distributionVolume === 'SPECIFIC_VOLUME' && volumeType) {
        if (!validSubmittedVolume(assignees, volumeType, metadata?.unassigned_count ?? 0)) {
          toast({
            title: 'Remaining volume must be fully distributed before submitting',
            status: 'warning',
            duration: 3000,
          });
          return;
        }
      }
    }

    setIsSubmitting(true);

    const resolvedVolumeType = isCompareMode
      ? 'MULTI_USER'
      : distributionVolume === 'EQUAL_PERCENTAGE'
        ? 'EQUAL_PERCENTAGE'
        : volumeType;

    try {
      // 1. Distribute work items in cqa-service
      const response = await fetchApi(`/api/cqa-service/v1/project/${ruleId}/assign-coders`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          volumeType: resolvedVolumeType,
          assignees: assignees.map((a) => ({ assigneeId: a.value, volume: a.volume })),
        }),
      });

      if (!response.ok) throw new Error(`Failed to assign coders (${response.status})`);

      // 2. Update the QA rule so isAssignedConfigured returns true on next load
      const updatedAudience = isCompareMode
        ? {
            ...(projectConfiguration?.audience ?? {}),
            individuals: assignees.map((a) => ({ value: a.value, label: a.label })),
            distributionMethod: 'individual',
          }
        : {
            ...(projectConfiguration?.audience ?? {}),
            individuals: assignees.map((a) => ({ value: a.value, label: a.label, volume: a.volume })),
            distributionMethod: 'individual',
            distributionVolume: distributionVolume,
            ...(volumeType ? { volumeType } : {}),
          };

      await apolloClient.mutate({
        mutation: UPDATE_QARULE,
        variables: {
          id: collectionId,
          qarule: {
            project_configuration: {
              ...(projectConfiguration ?? {}),
              audience: updatedAudience,
            },
          },
        },
      });

      toast({ title: 'Coders assigned successfully', status: 'success', duration: 3000 });
      window.dispatchEvent(new CustomEvent(QA_RULE_UPDATED_EVENT));
      onClose();
    } catch (err) {
      console.error('Error assigning coders:', err);
      toast({ title: 'Failed to assign coders', status: 'error', duration: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReassign =
    Array.isArray(projectConfiguration?.audience?.individuals) &&
    (projectConfiguration?.audience?.individuals?.length ?? 0) > 0;
  const modalTitle = isReassign
    ? `Re-assign Coders — ${projectName}`
    : `Assign Coders — ${projectName}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent padding={'10px'}>
        <Stack direction={'column'}>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalCloseButton />
        </Stack>
        <ModalBody>
          {/* Metadata loading / warnings */}
          {isMetadataLoading && (
            <HStack mb={3}>
              <Spinner size="sm" />
              <Text fontSize="sm" color="gray.500">Loading project info…</Text>
            </HStack>
          )}
          {!isMetadataLoading && metadata?.assignable_count === 0 && (
            <Alert status="warning" mb={3} borderRadius="md">
              <AlertIcon />
              There are no assignable work items.
            </Alert>
          )}
          {!isMetadataLoading && (metadata?.processing_count ?? 0) > 0 && (
            <Alert status="warning" mb={3} borderRadius="md">
              <AlertIcon />
              Work item tasks are still processing — please check back shortly.
            </Alert>
          )}

          {isLoading ? (
            <Box textAlign="center" py="2rem">
              <Spinner />
            </Box>
          ) : coders.length === 0 ? (
            <Text color="gray.500">No coders available for this group.</Text>
          ) : (
            <Stack spacing={4}>
              {/* Distribution volume — hidden in compare mode (every coder gets everything) */}
              {!isCompareMode && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">Distribution volume</FormLabel>
                  <RadioGroup value={distributionVolume} onChange={handleDistributionVolumeChange}>
                    <HStack spacing={6}>
                      <Radio value="EQUAL_PERCENTAGE" isDisabled={disableDistribution}>
                        Equal Percentages
                      </Radio>
                      <Radio value="SPECIFIC_VOLUME" isDisabled={disableDistribution}>
                        Specific Volume
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              )}

              {/* Volume type (conditional on Specific Volume, never shown in compare mode) */}
              {!isCompareMode && distributionVolume === 'SPECIFIC_VOLUME' && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">Volume type</FormLabel>
                  <Select
                    placeholder="Please select"
                    value={volumeType}
                    onChange={handleVolumeTypeChange}
                    isDisabled={disableDistribution}
                    size="sm"
                  >
                    <option value="PERCENTAGES">Percentages</option>
                    <option value="COUNT">Case Count</option>
                  </Select>
                </FormControl>
              )}

              {/* Selected assignees with volume inputs */}
              {assignees.length > 0 && (
                <Stack spacing={2}>
                  {assignees.map((entry) => (
                    <HStack key={entry.value} justify="space-between">
                      <Text fontSize="sm" flex="1" noOfLines={1}>{entry.label}</Text>
                      {!isCompareMode && (
                        <NumberInput
                          size="sm"
                          value={entry.volume}
                          min={0}
                          onChange={(valStr, valNum) => handleVolumeChange(entry, valStr, valNum)}
                          isDisabled={distributionVolume === 'EQUAL_PERCENTAGE'}
                          width="80px"
                        >
                          <NumberInputField />
                        </NumberInput>
                      )}
                      <IconButton
                        aria-label={`Remove ${entry.label}`}
                        icon={<CloseIcon boxSize="10px" />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleUserRemove(entry)}
                        data-testid={`remove-coder-${entry.value}`}
                      />
                    </HStack>
                  ))}
                  {distributionVolume === 'SPECIFIC_VOLUME' && volumeType && (
                    <Text fontSize="xs" textAlign="right" color="gray.600">
                      Remaining amount to distribute: {remainingVolume}
                      {volumeType === 'PERCENTAGES' ? '%' : ' Cases'}
                    </Text>
                  )}
                </Stack>
              )}

              {/* Add coder search autocomplete */}
              {(() => {
                const isCoderInputDisabled =
                  disableDistribution ||
                  (!isCompareMode && distributionVolume === 'SPECIFIC_VOLUME' && !volumeType);

                // Search candidates = coders fetched for the group, plus any
                // pre-existing assignees from the saved rule (re-assign flow)
                // that aren't in the fetched list — so a removed coder is always
                // searchable/re-addable.
                const preExisting: any[] = Array.isArray(projectConfiguration?.audience?.individuals)
                  ? projectConfiguration?.audience?.individuals
                  : [];
                const candidatePool: CoderOption[] = [...coders];
                preExisting.forEach((ind) => {
                  if (!candidatePool.some((c) => c.value === ind.value)) {
                    candidatePool.push({
                      value: ind.value ?? '',
                      label: ind.label ?? '',
                      username: ind.username ?? '',
                    });
                  }
                });

                const query = coderQuery.toLowerCase().normalize('NFC');
                const seen = new Set<string>();
                const filteredCoders = candidatePool.filter((c) => {
                  if (assignees.some((a) => a.value === c.value)) return false;
                  if (seen.has(c.value)) return false;
                  seen.add(c.value);
                  return (
                    (c.label ?? '').toLowerCase().normalize('NFC').includes(query) ||
                    (c.username ?? '').toLowerCase().normalize('NFC').includes(query)
                  );
                });

                return (
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">Add coder</FormLabel>
                    <Box position="relative">
                      <InputGroup size="sm">
                        <Input
                          placeholder="Search users"
                          value={coderQuery}
                          onChange={(e) => setCoderQuery(e.target.value)}
                          onFocus={() => setIsCoderListOpen(true)}
                          onBlur={() => setIsCoderListOpen(false)}
                          isDisabled={isCoderInputDisabled}
                          data-testid="coder-search-dropdown"
                        />
                        {isLoading && (
                          <InputRightElement>
                            <Spinner size="sm" />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      {isCoderListOpen && !isCoderInputDisabled && filteredCoders.length > 0 && (
                        <List
                          position="absolute"
                          zIndex={1700}
                          top="100%"
                          left={0}
                          right={0}
                          mt={1}
                          bg="white"
                          borderWidth="1px"
                          borderRadius="md"
                          boxShadow="md"
                          maxH="200px"
                          overflowY="auto"
                        >
                          {filteredCoders.map((c) => (
                            <ListItem
                              key={c.value}
                              px={3}
                              py={2}
                              cursor="pointer"
                              _hover={{ bg: 'gray.100' }}
                              // onMouseDown (before input blur) so the click registers
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleUserSelect(c);
                              }}
                              data-testid={`coder-option-${c.value}`}
                            >
                              <Text fontSize="sm">{c.label}</Text>
                              {c.username && (
                                <Text fontSize="12px" color="gray.500">{c.username}</Text>
                              )}
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </FormControl>
                );
              })()}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={assignees.length === 0 || isLoading || disableDistribution}
          >
            Assign ({assignees.length})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AssignCodersModal.displayName = 'AssignCodersModal';
export default AssignCodersModal;
